// assets/src/api/manage.js
//
// Customer-facing "manage reservation" API. Calls go through the WP
// admin-ajax proxy (nalu_manage_auth / nalu_manage_update) which forwards
// to the portal's /api/wp/reservation/auth and /api/wp/reservation/update
// routes. Wire-format mirrors docs/openapi/wp.yaml in the portal repo.
//
// Errors are thrown as ManageAuthError / ManageUpdateError with a `.kind`
// the UI branches on, so callers never have to parse messages. The PHP
// handler is the single classifier — it maps portal error codes to the
// kind strings below and supplies the customer-facing message.

import { postNaluAjax } from './ajax';

/**
 * Strip non-alphanumerics + uppercase. Mirrors the canonicalization the
 * PHP shortcode applies to `?manageres=<code>` so the JS sees the same
 * shape regardless of whether the code came from the URL or manual entry.
 */
export function canonicalizeCode(input) {
    return String(input || '').replace(/[^A-Za-z0-9]/g, '').toUpperCase();
}

const CODE_PATTERN = /^[A-HJKMNP-Z2-9]{6}$/;

export class ManageAuthError extends Error {
    constructor(kind, message) {
        super(message);
        this.name = 'ManageAuthError';
        // 'invalid_code' | 'malformed_code' | 'rate_limited' | 'manage_disallowed' | 'network'
        this.kind = kind;
    }
}

export class ManageUpdateError extends Error {
    constructor(kind, message) {
        super(message);
        this.name = 'ManageUpdateError';
        // 'session_expired' | 'forbidden_field' | 'bad_request' | 'network'
        this.kind = kind;
    }
}

const naluDebug = (...args) => {
    if (typeof window !== 'undefined' && window.naluBookingVars?.debug) {
        // eslint-disable-next-line no-console
        console.log('[manage]', ...args);
    }
};

// -- AJAX plumbing ------------------------------------------------------------

// Manage can be the first call on a stale/cached page (deep-link ?manageres=),
// so it heals a dead nonce too — via the shared nonce-healing POST helper.
function wpAjaxPost(body) {
    return postNaluAjax(body);
}

function throwFromEnvelope(ErrorCtor, json, fallbackMessage) {
    const kind = json?.data?.kind || 'network';
    const message = json?.data?.message || fallbackMessage;
    throw new ErrorCtor(kind, message);
}

// -- Public API ---------------------------------------------------------------

/**
 * Resolve a manage code into reservation + session token.
 *
 * Local-shape check runs first so garbage input never costs a round-trip
 * — the portal repeats the check server-side.
 */
export async function authenticate(rawCode) {
    const code = canonicalizeCode(rawCode);
    naluDebug('authenticate', { codeLength: code.length });

    if (!CODE_PATTERN.test(code)) {
        throw new ManageAuthError(
            'malformed_code',
            'Code must be 6 letters or numbers (no zero, one, O, I, or L).',
        );
    }

    const body = new FormData();
    body.append('action', 'nalu_manage_auth');
    body.append('security', window.naluBookingVars.security);
    body.append('code', code);

    let json;
    try {
        json = await wpAjaxPost(body);
    } catch (err) {
        throw new ManageAuthError('network', err?.message || 'Network error.');
    }

    if (!json?.success) {
        throwFromEnvelope(ManageAuthError, json, "We couldn't reach the reservation service.");
    }
    return json.data;
}

/**
 * Apply customer-edited fields to an existing reservation.
 *
 * `payload.passengers` is sparse: only mutated fields per id. On a 401
 * the caller is responsible for re-auth + retry (ManageReservation.js
 * already does this via the `session_expired` branch).
 */
export async function update(sessionToken, payload) {
    naluDebug('update', {
        hasToken: !!sessionToken,
        passengerCount: payload?.passengers?.length ?? 0,
    });

    if (!sessionToken) {
        throw new ManageUpdateError(
            'session_expired',
            'Your session has expired. Please re-enter your code.',
        );
    }

    // Portal /update validates `Number.isInteger(id)` on reservation and
    // each passenger; DOM datasets (row.dataset.paxId) return strings, so
    // coerce here at the wire boundary rather than in the component.
    const passengers = (payload.passengers || []).map(p => ({ ...p, id: Number(p.id) }));

    const body = new FormData();
    body.append('action', 'nalu_manage_update');
    body.append('security', window.naluBookingVars.security);
    body.append('session_token', sessionToken);
    body.append('reservation_id', String(Number(payload.reservationId)));
    body.append('passengers', JSON.stringify(passengers));

    let json;
    try {
        json = await wpAjaxPost(body);
    } catch (err) {
        throw new ManageUpdateError('network', err?.message || 'Network error.');
    }

    if (!json?.success) {
        throwFromEnvelope(ManageUpdateError, json, "We couldn't reach the reservation service.");
    }
    return json.data;
}
